import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PostMirrorCompletionLocationState, PostMirrorCompletionRequest } from './types';

// The navigation state is whatever the previous route put there - anything that is not the shape the
// mirroring flow sends is not a request to complete anything.
const parseRequest = (state: unknown): PostMirrorCompletionRequest | undefined => {
    const request = (state as PostMirrorCompletionLocationState | null)?.postMirrorCompletion;

    if (!request?.sourceMessageType || !request?.messageType) return undefined;

    return request;
};

const usePostMirrorCompletionRequest = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const request = useMemo(() => parseRequest(location.state), [location.state]);

    // The request lives in the navigation state, so dropping it keeps a refresh from raising the
    // prompt again on a declaration already dealt with. Only once the prompt is done with it: the
    // form tab remounts while the declaration is fetched (see `DeclarationPage`), so a request
    // cleared any earlier never gets served. The URL is rebuilt as-is because `navigate` replaces
    // all of it, and only the one key this flow owns is taken out of the state around it.
    const clearRequest = useCallback(() => {
        const state = location.state as PostMirrorCompletionLocationState | null;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: { ...state, postMirrorCompletion: undefined },
        });
    }, [navigate, location.pathname, location.search, location.state]);

    return { request, clearRequest };
};

export default usePostMirrorCompletionRequest;
